import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Users, UserPlus, Activity, CheckCircle2, UserCheck,
  Calendar, Coins, Lightbulb, BarChart3, MessageSquare, Bell, Trophy,
  RefreshCw, TrendingUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const apiBase = process.env.REACT_APP_BACKEND_URL || "";

/* ----------------- primitives ----------------- */

function KPICard({ icon: Icon, value, label, sub, color = "#00e5ff", testid }) {
  return (
    <div className="bg-black p-5 hover:bg-white/[0.02] transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <Icon size={15} className="text-white/30" strokeWidth={1.5} />
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-outfit font-semibold">{label}</span>
      </div>
      <div className="font-outfit font-black text-3xl tracking-tighter leading-none" style={{ color }}>
        {value}
      </div>
      {sub && <div className="text-[11.5px] text-white/40 mt-1.5 leading-snug">{sub}</div>}
    </div>
  );
}

function SectionHeader({ color, label, title }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-6 h-px" style={{ background: color }} />
        <span className="font-outfit text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color }}>
          {label}
        </span>
      </div>
      <h3 className="font-outfit font-bold text-xl text-white tracking-tight">{title}</h3>
    </div>
  );
}

function Sparkline({ data, color = "#00e5ff" }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data.map((d) => d.count), 1);
  const w = 100, h = 36;
  const step = w / (data.length - 1);
  const points = data.map((d, i) => `${i * step},${h - (d.count / max) * h}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-10" data-testid="kpi-posts-sparkline">
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon fill="url(#sparkFill)" points={`0,${h} ${points} ${w},${h}`} />
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
      {data.map((d, i) => (
        <circle key={d.day} cx={i * step} cy={h - (d.count / max) * h} r={i === data.length - 1 ? 2.2 : 1.2} fill={color} />
      ))}
    </svg>
  );
}

function formatEUR(n) {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
}
function formatDay(iso) {
  try {
    return new Date(iso).toLocaleDateString("de-DE", { weekday: "short" });
  } catch { return ""; }
}
function initialsFrom(name) {
  return name.split(/\s+/).slice(0, 2).map((n) => (n[0] || "").toUpperCase()).join("") || "??";
}

/* ----------------- main ----------------- */

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const res = await axios.get(`${API}/admin/dashboard/kpis`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "KPIs konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/[0.05] border border-white/[0.06]" data-testid="kpi-loading">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={`kpi-skel-${i}`} className="bg-black h-28 animate-pulse" />
        ))}
      </div>
    );
  }
  if (err) return <div className="border border-[#ef4444]/40 bg-[#ef4444]/5 p-4 text-[13px] text-[#ef4444]" data-testid="kpi-error">{err}</div>;
  if (!data) return null;

  const { community, events, engagement } = data;

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      {/* Refresh header */}
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-white/40 font-outfit">
          Stand: {new Date(data.generated_at).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}
        </p>
        <button
          onClick={load}
          data-testid="kpi-refresh"
          className="inline-flex items-center gap-2 px-3 h-9 border border-white/10 hover:border-[#00e5ff]/40 hover:text-[#00e5ff] text-white/60 text-[12px] font-outfit transition-colors"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Aktualisieren
        </button>
      </div>

      {/* ============ A · COMMUNITY ============ */}
      <section data-testid="kpi-section-community">
        <SectionHeader color="#00e5ff" label="A · Community" title="Community-Health" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/[0.05] border border-white/[0.06]">
          <KPICard
            icon={Users} value={community.total_members} label="Total" color="#00e5ff"
            sub="freigeschaltete Mitglieder"
            testid="kpi-total-members"
          />
          <KPICard
            icon={Activity} value={community.active_30d}
            label="Aktiv 30d" color="#2ecc71"
            sub={`${community.active_7d} in den letzten 7 Tagen`}
            testid="kpi-active"
          />
          <KPICard
            icon={UserPlus} value={community.new_30d}
            label="Neu 30d" color="#00e5ff"
            sub={`+${community.new_7d} in den letzten 7 Tagen`}
            testid="kpi-new"
          />
          <KPICard
            icon={UserCheck} value={community.pending_count}
            label="Pending" color={community.pending_count > 0 ? "#f39c12" : "#666"}
            sub="warten auf Freischaltung"
            testid="kpi-pending"
          />
        </div>
        {/* Profile completeness bar */}
        <div className="mt-3 border border-white/[0.06] bg-black p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-[#00e5ff]" />
              <span className="text-[12px] font-outfit uppercase tracking-[0.2em] text-white/60 font-semibold">
                Profil-Vervollständigung
              </span>
            </div>
            <span className="text-[13px] text-white font-outfit font-bold">
              {community.profile_complete_pct}% <span className="text-white/40 font-normal">({community.profile_complete_count} / {community.total_members})</span>
            </span>
          </div>
          <div className="h-[4px] bg-white/[0.06] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#00e5ff] to-[#2ecc71] transition-all duration-700"
              style={{ width: `${community.profile_complete_pct}%` }}
            />
          </div>
        </div>
      </section>

      {/* ============ B · EVENTS ============ */}
      <section data-testid="kpi-section-events">
        <SectionHeader color="#a855f7" label="B · Events" title="Events & Umsatz" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-white/[0.05] border border-white/[0.06]">
          <KPICard
            icon={Calendar} value={events.upcoming_count}
            label="Kommend" color="#a855f7"
            sub={`Ø ${events.avg_fill_pct}% ausgelastet`}
            testid="kpi-upcoming"
          />
          <KPICard
            icon={BarChart3} value={events.avg_attendees_30d}
            label="Ø TN / Event" color="#a855f7"
            sub="letzte 30 Tage"
            testid="kpi-avg-attendees"
          />
          <KPICard
            icon={Coins} value={formatEUR(events.revenue_mtd_eur)}
            label="Umsatz MTD" color="#2ecc71"
            sub="dieser Monat"
            testid="kpi-revenue-mtd"
          />
          <KPICard
            icon={TrendingUp} value={formatEUR(events.revenue_ytd_eur)}
            label="Umsatz YTD" color="#2ecc71"
            sub="laufendes Jahr"
            testid="kpi-revenue-ytd"
          />
          <KPICard
            icon={Lightbulb} value={events.event_suggestions_open}
            label="Vorschläge" color={events.event_suggestions_open > 0 ? "#f39c12" : "#666"}
            sub="offen zur Prüfung"
            testid="kpi-suggestions"
          />
          {/* Utilization bar card */}
          <div className="bg-black p-5 flex flex-col justify-between">
            <div className="flex items-start justify-between mb-2">
              <Calendar size={15} className="text-white/30" strokeWidth={1.5} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-outfit font-semibold">Auslastung</span>
            </div>
            <div>
              <div className="font-outfit font-black text-2xl tracking-tighter text-[#a855f7] mb-2">
                {events.avg_fill_pct}%
              </div>
              <div className="h-[3px] bg-white/[0.07] overflow-hidden">
                <div className="h-full bg-[#a855f7]" style={{ width: `${events.avg_fill_pct}%` }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ C · ENGAGEMENT ============ */}
      <section data-testid="kpi-section-engagement">
        <SectionHeader color="#f39c12" label="C · Engagement" title="Aktivität & Interaktion" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Posts sparkline */}
          <div className="border border-white/[0.06] bg-black p-5 lg:col-span-1">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-outfit font-semibold mb-1">Posts</div>
                <div className="font-outfit font-black text-3xl tracking-tighter text-[#f39c12]">
                  {engagement.posts_total_7d}
                </div>
                <div className="text-[11.5px] text-white/40 mt-0.5">letzte 7 Tage</div>
              </div>
              <MessageSquare size={15} className="text-white/30" />
            </div>
            <Sparkline data={engagement.posts_sparkline} color="#f39c12" />
            <div className="flex justify-between text-[10px] text-white/25 font-outfit mt-1">
              {engagement.posts_sparkline.map((d) => (
                <span key={d.day}>{formatDay(d.day)}</span>
              ))}
            </div>
          </div>

          {/* Chat + Push */}
          <div className="grid grid-rows-2 gap-4">
            <KPICard
              icon={MessageSquare} value={engagement.chat_messages_24h}
              label="Chat 24h" color="#00e5ff"
              sub="Nachrichten (DMs + Gruppen)"
              testid="kpi-chat-24h"
            />
            <KPICard
              icon={Bell} value={engagement.push_subscribers}
              label="Push-Abos" color="#a855f7"
              sub="aktive Service-Worker"
              testid="kpi-push-subs"
            />
          </div>

          {/* Top Members */}
          <div className="border border-white/[0.06] bg-black p-5" data-testid="kpi-top-members">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Trophy size={14} className="text-[#f39c12]" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-white/55 font-outfit font-semibold">
                  Top aktive Mitglieder (30d)
                </span>
              </div>
            </div>
            {engagement.top_active_members.length === 0 ? (
              <p className="text-[12px] text-white/35">Noch keine Posts in den letzten 30 Tagen.</p>
            ) : (
              <ol className="space-y-3">
                {engagement.top_active_members.map((m, idx) => {
                  const src = m.avatar_url
                    ? (m.avatar_url.startsWith("/") ? `${apiBase}${m.avatar_url}` : m.avatar_url)
                    : "";
                  return (
                    <li key={m.id} className="flex items-center gap-3">
                      <span className="w-5 text-right text-[11px] font-outfit font-bold text-white/35 tabular-nums">
                        {idx + 1}.
                      </span>
                      <div className="w-7 h-7 rounded-full overflow-hidden border border-white/15 bg-white/[0.03] flex items-center justify-center shrink-0">
                        {src
                          ? <img src={src} alt={m.name} className="w-full h-full object-cover" />
                          : <span className="text-[9.5px] font-outfit font-bold text-white/70 tracking-wider">{initialsFrom(m.name)}</span>}
                      </div>
                      <span className="flex-1 text-[13px] text-white/85 truncate font-outfit">{m.name || "Unbekannt"}</span>
                      <span className="text-[11px] text-[#f39c12] font-outfit font-bold tabular-nums">{m.post_count}</span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
